import {
  WSButton,
  WSElement,
  WSElementProps,
  WSFlexBox,
  WSGrid,
  WSList,
  WSMessageBox,
  WSText
} from "@wingspanhq/fe-component-library";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { queriesPayerEngagements, queriesPayerRows } from "@query";
import { selectorSearchRowName } from "../../../shared/selectors/selectorSearchRowName";
import { PAYERS_PATH } from "../../../modules/Payers/paths";
import { routeHelpersWorkLogsPayee } from "../../../modules/WorkLogs/paths";
import { useAddClientModal } from "../../../modules/Payers/components/AddClientModal/useAddClientModal";
import { SelectEngagementForPayer } from "../../../modules/Payers/components/SelectEngagementForPayer";
import { SelectPayer } from "../../../modules/Payers/components/SelectPayer";
import { AdditionalEmailsFieldOld } from "../AdditionalEmailsField/AdditionalEmailsFieldOld";
import { useInvoicesFormContext } from "./InvoicesForm";
import { selectorIsPayeeAllowedToInitiateInvoice } from "../../selectors/selectorIsPayeeAllowedToInitiateInvoice";
import { useUserId } from "../../../query/hooks/helpers";

export type PayerSectionProps = WSElementProps;

export type PayerSectionValues = {
  payer?: {
    payerId: string | null;
    payerEngagementId: string | null;
    emailsCC: { email: string }[];
  };
};

export const PayerSection: React.FC<PayerSectionProps> = props => {
  const history = useHistory();
  const { register, setValue, clearErrors, errors, watch } = useFormContext();
  const {
    defaultValues,
    invoicingConfigForPayee,
    payerRow,
    invoice,
    invoiceTemplate
  } = useInvoicesFormContext();
  const modalAddClient = useAddClientModal();

  const payerId = watch("payer.payerId");
  const payerEngagementId = watch("payer.payerEngagementId");

  useEffect(() => {
    if (payerRow) {
      setValue(
        "payer.emailsCC",
        (payerRow?.notificationCcEmails || []).map(email => ({ email }))
      );
    }
  }, [payerRow, setValue]);

  const [autoFocus, setAutoFocus] = useState(false);
  const preselectedEmail = defaultValues?.email;

  // A preselected email resolves to a client through the search rows (the only
  // email-addressable read); its assignment then auto-selects when there is just
  // one. Anything less certain focuses the picker instead of guessing.
  const queryPreselectedPayerRows =
    queriesPayerRows.searchPayerRows.usePaginatedQuery(
      {
        filter: {
          searchString: preselectedEmail ? { eq: preselectedEmail } : undefined
        }
      },
      { enabled: !!preselectedEmail }
    );

  useEffect(() => {
    if (!preselectedEmail || !queryPreselectedPayerRows.isSuccess) {
      return;
    }

    const payer = (queryPreselectedPayerRows.flatData ?? []).find(
      payer => payer.email === preselectedEmail
    );

    if (payer) {
      setAutoFocus(false);
      setValue("payer.payerId", payer.id);
    } else {
      setAutoFocus(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedEmail, queryPreselectedPayerRows.isSuccess]);

  const isDisabled = !!invoice || !!invoiceTemplate;

  // Selecting the client drops the assignment: an assignment only exists in the
  // context of the client it belongs to.
  const selectPayer = (newPayerId: string | null) => {
    clearErrors("payer");
    setValue("payer.payerId", newPayerId);
    setValue("payer.payerEngagementId", null);
  };

  return (
    <WSElement {...props}>
      <WSText.Heading5 mb="XL">Client assignment</WSText.Heading5>

      <input type="hidden" name="payer.payerId" ref={register()} />
      <input type="hidden" name="payer.payerEngagementId" ref={register()} />

      <WSList gap="M">
        <SelectPayer
          label="Client"
          placeholder="Select a client"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus={autoFocus}
          defaultSearchText={preselectedEmail}
          disabled={isDisabled}
          value={payerId ?? null}
          onChange={selectPayer}
          status={errors?.payer?.payerId ? "error" : undefined}
          message={errors?.payer?.payerId?.message}
          menuFooterText="Don't see a Client name? Add a new client to your client list."
          menuFooterAction={{
            label: "Add client",
            onClick: async () => {
              const payer = await modalAddClient.open();

              if (payer) {
                selectPayer(payer.id ?? null);
              }
            }
          }}
        />

        <SelectEngagementForPayer
          label="Assignment"
          payerId={payerId}
          disabled={isDisabled || !payerId}
          value={payerEngagementId ?? null}
          onChange={newValue => {
            clearErrors("payer");
            setValue("payer.payerEngagementId", newValue);
          }}
          autoSelectSingleOption
          status={errors?.payer?.payerEngagementId ? "error" : undefined}
          message={errors?.payer?.payerEngagementId?.message}
        />
      </WSList>

      <WSElement colorBackground="gray50" px="M" py="S" mt="M">
        <WSText.ParagraphXs weight="medium" color="gray600" mb="XS">
          Don't see an assignment?
        </WSText.ParagraphXs>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
          <WSText.ParagraphXs color="gray400">
            Some assignments require work logging
          </WSText.ParagraphXs>
          <WSButton
            kind="Link"
            size="S"
            rightIcon="chevron-right"
            onClick={() => history.push(routeHelpersWorkLogsPayee.path)}
          >
            Create work log
          </WSButton>
        </WSFlexBox.CenterY>
        <WSFlexBox.CenterY wrap="nowrap" justify="space-between">
          <WSText.ParagraphXs color="gray400">
            Some assignments are managed by clients directly.
          </WSText.ParagraphXs>
          <WSButton
            kind="Link"
            size="S"
            rightIcon="chevron-right"
            onClick={() => history.push(PAYERS_PATH)}
          >
            View clients
          </WSButton>
        </WSFlexBox.CenterY>
      </WSElement>

      <PreviewPayerEngagement />

      {invoicingConfigForPayee?.additionalGuidelines.enabled &&
      invoicingConfigForPayee?.additionalGuidelines?.value ? (
        <WSMessageBox.Info
          data-testid="additionalGuidelines"
          my="M"
          noBorder
          // title={`Guidelines from Client (${memberClient?.name || "-"}):`}
        >
          <WSText.ParagraphSm color="gray500" weight="book">
            {invoicingConfigForPayee?.additionalGuidelines.value}
          </WSText.ParagraphSm>
        </WSMessageBox.Info>
      ) : null}
    </WSElement>
  );
};

const PreviewPayerEngagement: React.FC = () => {
  const { payerRow, invoicingConfigForPayee } = useInvoicesFormContext();
  const { watch } = useFormContext();
  const userId = useUserId();

  const payerId = watch("payer.payerId");
  const payerEngagementId = watch("payer.payerEngagementId");

  // The engagement name comes from the regular read, not the search row, so it
  // matches whatever the assignment picker is showing.
  const queryPayerEngagement =
    queriesPayerEngagements.getPayerEngagement.useQuery(
      {
        payerId: payerId as string,
        payerEngagementId: payerEngagementId as string
      },
      { enabled: !!payerId && !!payerEngagementId }
    );

  const isPayeeInitiatedInvoicesNotAllowed =
    !selectorIsPayeeAllowedToInitiateInvoice(invoicingConfigForPayee, userId);

  return (
    <>
      {payerRow ? (
        <WSGrid mt="M" mb="L" gutter="M">
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Contact name
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {selectorSearchRowName(payerRow) || "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Client company
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {payerRow.profile?.companyName ||
                payerRow.profile?.doingBusinessAs ||
                "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Contact email
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>{payerRow.email}</WSText.ParagraphSm>
          </WSGrid.Item>
          <WSGrid.Item span={{ m: "6" }}>
            <WSText.ParagraphSm mb="S" color="gray500">
              Assignment name
            </WSText.ParagraphSm>
            <WSText.ParagraphSm>
              {queryPayerEngagement.data?.engagementName || "–"}
            </WSText.ParagraphSm>
          </WSGrid.Item>
        </WSGrid>
      ) : null}

      <AdditionalEmailsFieldOld
        hidden={!payerRow}
        name="payer.emailsCC"
        inputLabel="Contact email CC"
        buttonLabel="Add email recipient"
        buttonDescription="Additional email addresses will be cc’d"
        disabled={isPayeeInitiatedInvoicesNotAllowed}
      />
    </>
  );
};
